import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as React from "react";
import { useSelector } from "react-redux";

import { KeyCodeEnum } from "constants/keyCode";
import { CannedResponseInputMethod } from "helpers/canneds";
import { placeCursor } from "helpers/input";
import { selectCannedResponses } from "store/entities/canned-responses";

import { ICannedResponsesProps } from "../canned-responses";

import { IProps as ITextareaProps, Textarea } from "./Textarea";
import { CannedResponses } from "./canned-responses";
import { getCannedResponseSearchPhrase, insertIntoText } from "./helpers";
import { cannedResponsesStyles } from "./styles";

interface IProps extends ITextareaProps {
  cannedResponsesByShortcuts: ICannedResponsesProps["items"];
  cannedResponsesVisible: boolean;
  children?: React.ReactNode;
  onTextareaRefChange?: (textarea: HTMLTextAreaElement) => void;
  onCannedResponseClose: (shouldPlaceCursor?: boolean) => void;
  onCannedResponseOpen: (isReopen?: boolean) => void;
  onCannedResponseSelect: (newValue: string, response: string, method: CannedResponseInputMethod) => void;
}

export const TextareaWithCannedResponses: React.FC<IProps> = (props: IProps) => {
  const {
    cannedResponsesByShortcuts,
    cannedResponsesVisible,
    children,
    disabled,
    maxRows,
    mobile,
    onBlur,
    onCannedResponseSelect,
    onCannedResponseClose,
    onCannedResponseOpen,
    onChange,
    onFocus,
    onInput,
    onKeyDown,
    onResize,
    placeholder,
    privateMode,
    onTextareaRefChange,
    value,
  } = props;

  const cannedResponses = useSelector(selectCannedResponses);
  const cannedResponsesEnabled = true;
  const [cannedResponseTriggerIndex, setCannedResponseTriggerIndex] = useState(-1);
  const [areSearchResultsEmpty, setAreSearchResultsEmpty] = useState(true);
  // @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
  const [textareaRef, setTextareaRef] = useState<HTMLTextAreaElement>(null);
  const textareaWithCannedResponsesRef = useRef<HTMLDivElement>(null);
  const cannedResponseSearchPhrase = useMemo(() => {
    const cursorPosition = textareaRef ? textareaRef.selectionEnd : 0;
    return getCannedResponseSearchPhrase(value, cannedResponseTriggerIndex, cursorPosition);
  }, [cannedResponseTriggerIndex, textareaRef, value]);

  const handleTextareaRefChange = useCallback(
    (ref: HTMLTextAreaElement) => {
      setTextareaRef(ref);
      // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      onTextareaRefChange(ref);
    },
    [onTextareaRefChange]
  );

  const handleCannedResponsesClose = useCallback(() => {
    setCannedResponseTriggerIndex(-1);
    onCannedResponseClose();
  }, [onCannedResponseClose]);

  const handleCannedResponsesSearchResultsCountChange = useCallback((count: number) => {
    setAreSearchResultsEmpty(count === 0);
  }, []);

  const handleTextareaValueChange = useCallback(
    (changeCallback: (e: React.FormEvent<HTMLTextAreaElement>) => void) =>
      (e: React.FormEvent<HTMLTextAreaElement>): void => {
        if (cannedResponsesEnabled) {
          const { value: newValue, selectionEnd } = e.currentTarget;
          const characterBeforeCursorIndex = newValue ? selectionEnd - 1 : -1;
          // Any new `#` will trigger fresh canned responses shortcuts mode
          if (newValue[characterBeforeCursorIndex] === "#") {
            setCannedResponseTriggerIndex(characterBeforeCursorIndex);
            onCannedResponseOpen();
          } else if (newValue.slice(0, selectionEnd).length <= cannedResponseTriggerIndex) {
            if (newValue[newValue.length - 1] === "#") {
              setCannedResponseTriggerIndex(newValue.length - 1);
            } else {
              setCannedResponseTriggerIndex(-1);
              onCannedResponseClose();
            }
          }
        }

        changeCallback(e);
      },
    [cannedResponseTriggerIndex, cannedResponsesEnabled, onCannedResponseClose, onCannedResponseOpen]
  );

  const handleTextAreaKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    const shouldPreventSendKey = cannedResponsesVisible && e.keyCode === KeyCodeEnum.Enter && !areSearchResultsEmpty;
    if (!shouldPreventSendKey) {
      onKeyDown?.(e);
    }
  };

  const handleCannedResponseSelect = useCallback(
    (response: string, method: CannedResponseInputMethod) => {
      const responseItem = cannedResponses[response];
      onCannedResponseSelect(
        insertIntoText(value, responseItem.text, cannedResponseTriggerIndex, cannedResponseSearchPhrase.length + 1),
        responseItem.text,
        method
      );
      onCannedResponseClose();
      setCannedResponseTriggerIndex(-1);
      setTimeout(() => placeCursor(textareaRef, cannedResponseTriggerIndex + responseItem.text.length), 0);
    },
    [
      cannedResponseSearchPhrase.length,
      cannedResponseTriggerIndex,
      onCannedResponseClose,
      onCannedResponseSelect,
      textareaRef,
      value,
    ]
  );

  const handleTextareaClick = (): void => {
    if (cannedResponsesVisible) {
      setCannedResponseTriggerIndex(-1);
      onCannedResponseClose(false);
    }
  };

  useEffect(() => {
    // This is the case when canned response visibility is triggered
    // outside this component. It requires us to save '#' position
    // manually and place cursor appropriately
    if (cannedResponsesVisible && cannedResponseTriggerIndex === -1) {
      const cursorPosition = textareaRef.selectionEnd;
      setCannedResponseTriggerIndex(cursorPosition);
      // This delay is required to prevent autopositioning of cursor
      // To be considered if this cursor placement shouldn't be a part of
      // external visibility handling by MessageBox
      setTimeout(() => placeCursor(textareaRef, cursorPosition + 1), 0);
    }
  }, [cannedResponseTriggerIndex, cannedResponsesVisible, textareaRef]);

  return (
    <div>
      {children}
      <div ref={textareaWithCannedResponsesRef}>
        {cannedResponsesVisible && (
          <CannedResponses
            css={cannedResponsesStyles}
            items={cannedResponsesByShortcuts}
            onClose={handleCannedResponsesClose}
            onSearchResultsCountChange={handleCannedResponsesSearchResultsCountChange}
            // @ts-expect-error TS(2322)
            onSelect={handleCannedResponseSelect}
            searchPhrase={cannedResponseSearchPhrase}
            textareaWithCannedResponsesRef={textareaWithCannedResponsesRef}
          />
        )}
        <Textarea
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          privateMode={privateMode}
          mobile={mobile}
          maxRows={maxRows}
          innerRef={handleTextareaRefChange}
          // @ts-expect-error TS(2345): Argument of type '((e: FormEvent<HTMLTextAreaEleme... Remove this comment to see the full error message
          onInput={handleTextareaValueChange(onInput)}
          // @ts-expect-error TS(2345): Argument of type '((e: FormEvent<HTMLTextAreaEleme... Remove this comment to see the full error message
          onChange={handleTextareaValueChange(onChange)}
          onKeyDown={handleTextAreaKeyDown}
          onClick={handleTextareaClick}
          onFocus={onFocus}
          onBlur={onBlur}
          onResize={onResize}
        />
      </div>
    </div>
  );
};
